import { authStore } from "@kaplan-labs/up-auth-api-client";

import { ownerStore, serverRoutes, themeStore } from "@up/data";

import type { TCart, TGalaxyError } from "@up/data";

type ApiResponse<T> = { data: T };

export type Config = {
  baseURL: string;
};

let _config: undefined | Config = undefined;

export function setNetworkConfig(c: Partial<Config>) {
  _config = {
    ...(_config as Config),
    ...c,
  };
}

function getConfig() {
  const theme = themeStore.get();
  const owner = ownerStore.get();
  const { baseURL } = _config as Config;

  return {
    auth: authStore.get(),
    baseURL,
    owner: owner?.email,
    ownerContactId: owner?.contactId,
    partnerID: theme.partnerID,
  };
}

export function fetchCart() {
  const { auth, baseURL, ownerContactId, partnerID } = getConfig();
  const URL = `${baseURL}${serverRoutes.checkout.getCart(partnerID)}`;

  return auth.client
    .get(URL, {
      searchParams: { errors: true, ...(ownerContactId && { ownerContactId }) },
    })
    .json<{ data: TCart; errors?: TGalaxyError[] }>();
}

export function saveStudent(student: TCart["student"]) {
  const { auth, baseURL, owner, ownerContactId, partnerID } = getConfig();
  const URL = `${baseURL}${serverRoutes.checkout.student(partnerID)}`;

  return auth.client
    .patch(URL, {
      json: { student, owner, ownerContactId },
    })
    .json<ApiResponse<TCart>>()
    .then((r) => r.data);
}

export function saveGuardian(guardian: TCart["guardian"]) {
  const { auth, baseURL, owner, ownerContactId, partnerID } = getConfig();
  const URL = `${baseURL}${serverRoutes.checkout.guardian(partnerID)}`;

  return auth.client
    .patch(URL, {
      json: {
        guardian,
        owner,
        ownerContactId,
      },
    })
    .json<ApiResponse<TCart>>()
    .then((r) => r.data);
}

export const addCoupon = function (code: string) {
  const { auth, baseURL, owner, ownerContactId, partnerID } = getConfig();
  const URL = `${baseURL}${serverRoutes.checkout.couponsAdd(partnerID)}`;

  return auth.client
    .post(URL, {
      json: {
        code,
        owner,
        ownerContactId,
      },
    })
    .json<ApiResponse<TCart>>()
    .then((r) => r.data);
};

const removeCoupon = function (code: string) {
  const { auth, baseURL, owner, ownerContactId, partnerID } = getConfig();
  const URL = `${baseURL}${serverRoutes.checkout.couponsRemove(partnerID, code)}`;

  return auth.client
    .delete(URL, {
      json: {
        owner,
        ownerContactId,
      },
    })
    .json<ApiResponse<TCart>>()
    .then((r) => r.data);
};

const addLineItem = function (id: string) {
  const { auth, baseURL, owner, ownerContactId, partnerID } = getConfig();
  const URL = `${baseURL}${serverRoutes.checkout.lineItemAdd(partnerID)}`;
  const json = {
    _id: id,
    owner,
    ownerContactId,
    partner: {
      _id: partnerID,
    },
  };

  return auth.client
    .post(URL, {
      json,
    })
    .json<ApiResponse<TCart>>();
};

// TODO: return type needs work
export const removeLineItem = function (_id: string) {
  const { auth, baseURL, owner, ownerContactId, partnerID } = getConfig();
  const URL = `${baseURL}${serverRoutes.checkout.lineItemRemove(partnerID, _id)}`;

  return auth.client
    .delete(URL, {
      json: {
        owner,
        ownerContactId,
      },
    })
    .json<ApiResponse<TCart>>();
};

export async function submitAsComplete() {
  const { auth, baseURL, owner, ownerContactId, partnerID } = getConfig();
  const URL = `${baseURL}${serverRoutes.checkout.complete(partnerID)}`;

  const response = await auth.client
    .post(URL, {
      json: { owner, ownerContactId },
    })
    .json<ApiResponse<unknown>>();

  return response.data;
}

export async function finalValidate() {
  const { auth, baseURL, ownerContactId, partnerID } = getConfig();
  const URL = `${baseURL}${serverRoutes.checkout.validate(partnerID)}`;

  const response = await auth.client
    .post(URL, {
      json: ownerContactId ? { ownerContactId } : {},
    })
    .json<ApiResponse<TCart>>();

  return response.data;
}

export const network = {
  setNetworkConfig,

  addLineItem,
  removeLineItem,

  addCoupon,
  removeCoupon,

  saveGuardian,
  saveStudent,

  fetchCart,
  finalValidate,
  submitAsComplete,
};

export type NetworkType = typeof network;
